```vue
<template>
  <div id="group1">
    <!-- Section title -->
    <h4 class="section-title">
      {{ header || 'Preset Regimens' }}
    </h4>

    <div class="preset-regimen-box">
      <span class="preset-result-count">
        Showing {{ regimens.items ? regimens.items.length : '0' }} results
      </span>

      <div>
        <div
          v-for="regimen in regimens.items"
          :key="regimen.id"
          :id="`regimen-card-${regimen.id}`"
          class="preset-regimen-card"
        >
          <!-- Regimen header row -->
          <div class="preset-regimen-header">
            <div class="preset-regimen-meta">
              <a
                class="preset-regimen-name"
                href="#"
                @click.prevent="showProducts(regimen)"
              >
                {{ regimen.name }}
                <svg
                  :class="['preset-chevron', { 'preset-chevron--open': regimen.showProducts }]"
                  width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2.5"
                  stroke-linecap="round" stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </a>
              <span class="preset-regimen-info">
                {{ regimen.products.length }} products &nbsp;·&nbsp; {{ regimen.practice.name }}
              </span>
              <div v-if="regimen.tags && regimen.tags.length" class="preset-tags">
                <span
                  v-for="tag in regimen.tags"
                  :key="tag"
                  class="preset-tag"
                >
                  {{ getTagName(tag).value }}
                </span>
              </div>
              <p v-if="regimen.description" class="preset-description">
                {{ regimen.description }}
              </p>
            </div>

            <!-- Bulk add buttons — add all products in this regimen at once -->
            <div class="preset-bulk-actions">
              <button
                class="time-btn"
                title="Add all to Morning"
                :disabled="isRegimenAllAdded(regimen, 'am')"
                :class="{ 'disabled': isRegimenAllAdded(regimen, 'am') }"
                @click="handleProducts(regimen.products, 'am')"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="4"/>
                  <line x1="12" y1="2"  x2="12" y2="4"/>
                  <line x1="12" y1="20" x2="12" y2="22"/>
                  <line x1="4.22" y1="4.22"   x2="5.64"  y2="5.64"/>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                  <line x1="2"  y1="12" x2="4"  y2="12"/>
                  <line x1="20" y1="12" x2="22" y2="12"/>
                  <line x1="4.22"  y1="19.78" x2="5.64"  y2="18.36"/>
                  <line x1="18.36" y1="5.64"  x2="19.78" y2="4.22"/>
                </svg>
                <span>AM</span>
              </button>

              <button
                class="time-btn"
                title="Add all to Evening"
                :disabled="isRegimenAllAdded(regimen, 'pm')"
                :class="{ 'disabled': isRegimenAllAdded(regimen, 'pm') }"
                @click="handleProducts(regimen.products, 'pm')"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
                <span>PM</span>
              </button>

              <button
                class="time-btn time-btn--both"
                title="Add all to Both AM & PM"
                :disabled="isRegimenAllAdded(regimen, 'both')"
                :class="{ 'disabled': isRegimenAllAdded(regimen, 'both') }"
                @click="handleProducts(regimen.products, 'both')"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="4"/>
                  <line x1="12" y1="2"  x2="12" y2="4"/>
                  <line x1="12" y1="20" x2="12" y2="22"/>
                  <line x1="4.22" y1="4.22"   x2="5.64"  y2="5.64"/>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                  <line x1="2"  y1="12" x2="4"  y2="12"/>
                  <line x1="20" y1="12" x2="22" y2="12"/>
                  <line x1="4.22"  y1="19.78" x2="5.64"  y2="18.36"/>
                  <line x1="18.36" y1="5.64"  x2="19.78" y2="4.22"/>
                </svg>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
                <span>Both</span>
              </button>
            </div>
          </div>

          <!-- Expanded product list — same card structure as "All Products" -->
          <div v-if="regimen.showProducts" class="preset-products-list">
            <div
              v-for="product in regimen.products"
              :key="product.id"
              class="product-wrapper"
              draggable="true"
              @dragstart="startDrag($event, product)"
            >
              <div class="product-item">
                <!-- Image + stacked name / vendor -->
                <div class="product-info-wrapper">
                  <img
                    :src="product.image || '/images/product-placeholder.jpg'"
                    :alt="product.title"
                    class="product-image"
                  >
                  <div class="product-details">
                    <p class="product-name">{{ product.title }}</p>
                    <p class="product-vendor">{{ product.vendor }}</p>
                  </div>
                </div>

                <!-- View Details + time buttons -->
                <div class="product-actions">
                  <a
                    href="#"
                    class="btn-view"
                    @click.prevent="toggleProductDetail(product.id)"
                  >
                    {{ expandedProduct === product.id ? 'Close Details' : 'View Details' }}
                  </a>

                  <div class="time-actions">
                    <!-- AM -->
                    <button
                      class="time-btn"
                      title="Morning"
                      :disabled="isInMorning(product)"
                      :class="{ 'disabled': isInMorning(product) }"
                      @click="emitProduct(product, 'am')"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="4"/>
                        <line x1="12" y1="2"  x2="12" y2="4"/>
                        <line x1="12" y1="20" x2="12" y2="22"/>
                        <line x1="4.22" y1="4.22"   x2="5.64"  y2="5.64"/>
                        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                        <line x1="2"  y1="12" x2="4"  y2="12"/>
                        <line x1="20" y1="12" x2="22" y2="12"/>
                        <line x1="4.22"  y1="19.78" x2="5.64"  y2="18.36"/>
                        <line x1="18.36" y1="5.64"  x2="19.78" y2="4.22"/>
                      </svg>
                    </button>
                    <!-- PM -->
                    <button
                      class="time-btn"
                      title="Evening"
                      :disabled="isInEvening(product)"
                      :class="{ 'disabled': isInEvening(product) }"
                      @click="emitProduct(product, 'pm')"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                      </svg>
                    </button>
                    <!-- AM + PM — only disabled when in both -->
                    <button
                      class="time-btn time-btn--both"
                      title="Both AM & PM"
                      :disabled="isInMorning(product) && isInEvening(product)"
                      :class="{ 'disabled': isInMorning(product) && isInEvening(product) }"
                      @click="emitProduct(product, 'both')"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="4"/>
                        <line x1="12" y1="2"  x2="12" y2="4"/>
                        <line x1="12" y1="20" x2="12" y2="22"/>
                        <line x1="4.22" y1="4.22"   x2="5.64"  y2="5.64"/>
                        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                        <line x1="2"  y1="12" x2="4"  y2="12"/>
                        <line x1="20" y1="12" x2="22" y2="12"/>
                        <line x1="4.22"  y1="19.78" x2="5.64"  y2="18.36"/>
                        <line x1="18.36" y1="5.64"  x2="19.78" y2="4.22"/>
                      </svg>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                      </svg>
                    </button>

                    <!-- Added badge only when in both sections -->
                    <span v-if="isInMorning(product) && isInEvening(product)" class="product-added-badge">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5eb052" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      Added
                    </span>
                  </div>
                </div>
              </div>

              <!-- Expandable description -->
              <div v-if="expandedProduct === product.id" class="product-description">
                <div v-if="product.shopify_product && product.shopify_product.body_html" class="description-content">
                  <h5>Description:</h5>
                  <div class="description-text" v-html="product.shopify_product.body_html" />
                </div>
                <div v-else class="description-content">
                  <p class="no-description">No description available for this product.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Pagination
      :total-rows="regimens.total_items"
      :total-pages="regimens.total_pages"
      :current-page="currentPage"
      :per-page="20"
      :hide-per-page="false"
      @change-page="changePage"
      @change-per-page="changePerPage"
    />
  </div>
</template>

<style scoped lang="scss">
/* ── SECTION WRAPPER ─────────────────────────────────── */
.preset-regimen-box {
  padding: 0 10px;
}

.preset-result-count {
  display: block;
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 12px;
  padding: 0 7px;
}

/* ── REGIMEN CARD ────────────────────────────────────── */
.preset-regimen-card {
  border: 1px solid #EBEBEB;
  border-radius: 12px;
  background: #fff;
  margin-bottom: 8px;
  overflow: hidden;
  transition: box-shadow .18s, border-color .18s;

  &:hover {
    border-color: #9F7ED9;
    box-shadow: 0 4px 18px rgba(159, 126, 217, .13);
  }
}

.preset-regimen-header {
  padding: 14px 20px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.preset-bulk-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding-top: 2px;
}

.preset-bulk-actions .time-btn {
  width: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: #F9F9F9;
  border: 1px solid #E4E4E4;
  border-radius: 8px;
  padding: 5px 10px;
  cursor: pointer;
  color: #374151;
  font-size: 12px;
  font-weight: 500;
  transition: border-color .15s, color .15s, background .15s;
  line-height: 1;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: #9F7ED9;
    color: #9F7ED9;
    background: #f5f0fd;
  }

  &.disabled,
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
    background: #f5f5f5;
  }
}

.preset-bulk-actions .time-btn--both {
  gap: 3px;
}

.preset-regimen-name {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  text-decoration: none;
  margin-bottom: 4px;

  &:hover {
    color: #9F7ED9;
  }
}

.preset-chevron {
  flex-shrink: 0;
  transition: transform .2s ease;
  color: #9ca3af;

  &--open {
    transform: rotate(180deg);
  }
}

.preset-regimen-info {
  display: block;
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 6px;
}

.preset-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 6px;
}

.preset-tag {
  display: inline-block;
  background: #f3effd;
  color: #7c52c8;
  font-size: 12px;
  font-weight: 500;
  padding: 2px 10px;
  border-radius: 999px;
}

.preset-description {
  font-size: 13px;
  color: #374151;
  margin: 4px 0 0;
}

/* ── PRODUCT LIST (inside expanded regimen) ──────────── */
.preset-products-list {
  border-top: 1px solid #EBEBEB;
  padding: 8px 10px;
  background: #FAFAFA;
}

/* Inherit the same .product-wrapper card style */
.preset-products-list .product-wrapper {
  border: 1px solid #EBEBEB;
  margin: 0 0 5px;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  cursor: grab;
  transition: box-shadow .18s, border-color .18s;

  &:hover {
    border-color: #9F7ED9;
    box-shadow: 0 4px 18px rgba(159, 126, 217, .13);
  }

  &:active {
    cursor: grabbing;
    opacity: 0.7;
  }
}

.preset-products-list .product-item {
  display: flex;
  align-items: center;
  gap: .75rem;
  padding: 10px 20px;
}

.preset-products-list .product-info-wrapper {
  display: flex;
  align-items: center;
  gap: .85rem;
  flex: 1;
  min-width: 0;
}

.preset-products-list .product-image {
  width: 40px;
  height: 40px;
  border: 1px solid #F2F2F2;
  object-fit: contain;
  flex-shrink: 0;
  border-radius: 6px;
  background: #f3f3f9;
}

.preset-products-list .product-details {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.preset-products-list .product-name {
  font-size: 16px;
  font-weight: 400;
  color: #6b7280;
  margin: 0 0 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preset-products-list .product-vendor {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preset-products-list .product-actions {
  display: flex;
  align-items: center;
  gap: 32px;
  flex-shrink: 0;
}

.preset-products-list .btn-view {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;
  transition: color .15s;

  &:hover {
    color: #9F7ED9;
  }
}

.preset-products-list .time-actions {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Re-use the same .time-btn style from the parent */
.preset-products-list .time-btn {
  width: 80px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  background: #F9F9F9;
  border: 1px solid #E4E4E4;
  border-radius: 8px;
  padding: 4px 12px;
  cursor: pointer;
  color: #374151;
  transition: border-color .15s, color .15s, background .15s;
  line-height: 1;
  white-space: nowrap;

  &:hover {
    border-color: #9F7ED9;
    color: #9F7ED9;
    background: #f5f0fd;
  }
}

.preset-products-list .time-btn--both {
  padding: .38rem .55rem;
}

/* "Added" badge */
.product-added-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #5eb052;
}

/* Expandable description */
.preset-products-list .product-description {
  border-top: 1px solid #EBEBEB;
  padding: .75rem 1rem;
  background: #fafafa;
}

.preset-products-list .description-content h5 {
  font-size: 12px;
  font-weight: 700;
  margin-bottom: .4rem;
  color: #333;
}

.preset-products-list .description-text {
  font-size: .83rem;
  color: #000;
  line-height: 1.5;
}

.preset-products-list .no-description {
  font-size: .83rem;
  color: #222;
  margin: 0;
}
</style>

<script>
import { mapGetters } from 'vuex';
import Pagination from './Paginations';
import { tags } from '@/store/regimen/initialObject';
import { encodeQueryData } from '@/helper';
import { GET_PRESET_REGIMENS } from '@/store/regimen/actions.types';

export default {
  name: 'PresetRegimenNew',

  components: {
    Pagination
  },

  props: {
    header: {
      type: String,
      default: '',
    },

    morningProducts: {
      type: Array,
      default: () => []
    },

    eveningProducts: {
      type: Array,
      default: () => []
    },

    patient: {
      type: Object,
      default() {
        return {};
      }
    },

    creators: {
      type: String,
      default: '',
    },

    concerns: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      sending: false,
      filters: {
        regimen_type: ['preset']
      },
      currentPage: 1,
      perPage: 20,
      regimenTags: tags,
      expandedProduct: null
    };
  },

  computed: {
    ...mapGetters([
      'regimens',
      'practice'
    ])
  },

  watch: {
    concerns() {
      this.currentPage = 1;

      if (this.concerns) {
        this.filters.tags = [this.concerns];
      } else {
        delete this.filters.tags;
      }

      this.loadPresets();
    },

    creators() {
      this.currentPage = 1;

      if (this.creators) {
        this.filters.creators = this.creators;
      } else {
        delete this.filters.creators;
      }

      this.loadPresets();
    }
  },

  beforeMount() {
    this.loadPresets();
  },

  methods: {
    toggleProductDetail(productId) {
      this.expandedProduct = this.expandedProduct === productId ? null : productId;
    },

    // Normalise a preset product into the same Shopify-style shape
    // that startDragProduct / addProductToSection in the parent expects.
    normaliseProduct(product) {
      return {
        id: product.shopify_product_graph_id || product.id,
        title: product.title,
        handle: product.handle || '',
        vendor: product.vendor || '',
        productType: product.productType || 'Product',
        variants: [
          {
            id: product.variant_graph_id || product.variant_id,
            title: product.variant_title || '',
            sku: product.sku || '',
            price: { amount: product.price || 0 }
          }
        ],
        images: {
          edges: [
            { node: { url: product.image || '' } }
          ]
        }
      };
    },

    startDrag(event, product) {
      const normalised = this.normaliseProduct(product);
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData('product', JSON.stringify(normalised));
      event.dataTransfer.setData('type', 'product');

      // Custom drag image — same style as the parent component
      const dragImage = document.createElement('div');
      dragImage.style.cssText = `
        position: absolute;
        top: -1000px;
        left: -1000px;
        background: white;
        border: 2px solid #9F7ED9;
        border-radius: 12px;
        padding: 16px 20px;
        box-shadow: 0 4px 12px rgba(159, 126, 217, 0.3);
        width: 660px;
        min-width: 660px;
        z-index: 10000;
      `;
      dragImage.innerHTML = `
        <div style="font-size: 14px; font-weight: 700; color: #111827; margin-bottom: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${product.title}</div>
        <div style="font-size: 12px; color: #6b7280; white-space: nowrap;">Let go to drop in position</div>
      `;
      document.body.appendChild(dragImage);
      event.dataTransfer.setDragImage(dragImage, 330, 30);
      setTimeout(() => { document.body.removeChild(dragImage); }, 0);
    },

    emitProduct(product, section) {
      const normalizedProduct = {
        id: product.shopify_product_graph_id || product.id,

        title: product.title,
        handle: product.handle || '',
        vendor: product.vendor || '',

        productType: product.productType || 'Product',

        variants: [
          {
            id: product.variant_graph_id || product.variant_id,
            title: product.variant_title || '',
            sku: product.sku || '',
            price: {
              amount: product.price || 0
            }
          }
        ],

        images: {
          edges: [
            {
              node: {
                url: product.image || ''
              }
            }
          ]
        }
      };

      this.$emit('open-preset-product', {
        product: normalizedProduct,
        section
      });
    },

    isAdded(product) {
      return this.isInMorning(product) && this.isInEvening(product);
    },

    // Check if a preset product is already in the morning section.
    isInMorning(product) {
      if (!this.morningProducts || !this.morningProducts.length) return false;
      return this.morningProducts.some(p => {
        if (!p.shopify_product_graph_id || !product.shopify_product_graph_id) return false;
        return p.shopify_product_graph_id === product.shopify_product_graph_id;
      });
    },

    // Check if a preset product is already in the evening section.
    isInEvening(product) {
      if (!this.eveningProducts || !this.eveningProducts.length) return false;
      return this.eveningProducts.some(p => {
        if (!p.shopify_product_graph_id || !product.shopify_product_graph_id) return false;
        return p.shopify_product_graph_id === product.shopify_product_graph_id;
      });
    },

    // Per-section bulk disable:
    // 'am'   → disabled when every product is already in morning
    // 'pm'   → disabled when every product is already in evening
    // 'both' → disabled when every product is in both morning AND evening
    isRegimenAllAdded(regimen, section) {
      if (!regimen.products || !regimen.products.length) return true;
      if (section === 'am') {
        return regimen.products.every(p => this.isInMorning(p));
      }
      if (section === 'pm') {
        return regimen.products.every(p => this.isInEvening(p));
      }
      return regimen.products.every(p => this.isInMorning(p) && this.isInEvening(p));
    },

    handleProduct(product, section) {
      this.emitProduct(product, section);
    },

    handleProducts(products, section) {
      products.forEach(product => {
        this.emitProduct(product, section);
      });
    },

    changePage(value) {
      this.currentPage = value;
      this.loadPresets();
    },

    changePerPage(value) {
      this.currentPage = 1;
      this.perPage = value;
      this.loadPresets();
    },

    buildRegimensParams() {
      return {
        filters: this.filters,
        page: this.currentPage,
        per: this.perPage,
      };
    },

    showProducts(regimen) {
      const regimenIndex = this.regimens.items.findIndex(
        el => el.id == regimen.id
      );

      const isOpening = !this.regimens.items[regimenIndex].showProducts;

      // Use Vue.set so the toggle is reactive without forcing a full list re-render
      this.$set(this.regimens.items[regimenIndex], 'showProducts', isOpening);

      if (isOpening) {
        this.$nextTick(() => {
          const card = document.getElementById(`regimen-card-${regimen.id}`);
          if (!card) return;
          card.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    },

    loadPresets() {
      if (this.creators) {
        this.filters.creators = this.creators;
      }

      if (this.sending) return;

      this.sending = true;

      this.$store.dispatch(
        GET_PRESET_REGIMENS,
        encodeQueryData(this.buildRegimensParams())
      ).finally(() => {
        this.sending = false;
      });
    },

    getTagName(key) {
      return this.regimenTags.find(tag => tag.name == key);
    },

    getPracticeId() {
      return this.patient && this.patient.practice
        ? this.patient.practice.id
        : this.practice.id;
    }
  }
};
</script>